import * as React from 'react';
import type {ReactNode} from 'react';
import {Fragment, useContext, useRef} from 'react';
import {provider} from '@sail/engine';
import * as Themed from '@sail/theme-classic';
import type {ReactAriaTreeState as TreeState} from '@sail/react-aria';
import type {AriaLabelingProps} from '@react-types/shared';
import {
  useFocusRing,
  FocusScope,
  PressResponder,
  useMenuTriggerState,
  useMenu,
  useMenuItem,
  useMenuTrigger,
  useMenuSection,
} from '@sail/react-aria';

import {
  useTreeState,
  useCollection,
  CollectionProvider,
  useItem,
  IndexedGroup,
} from './collections';
import type {ActionCallback, CollectionItemId} from './collections/types';
import {Popover} from './layers/Popover';

import {view} from '../view';

export interface MenuProps extends AriaLabelingProps {
  /**
   * Handler that is called when an item is selected.
   * @external
   */
  onAction?: (key: CollectionItemId) => void;
  /**
   * Handler that is called when the menu closes.
   * @external
   */
  onClose?: () => void;
  /**
   * Handler that is called when the menu opens/closes.
   * @external
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The trigger Element to show/hide the menu. Must be a component that supports press events, such as a Button or Link.
   * @external
   */
  trigger?: ReactNode;
  /** The contents of the menu. */
  children: ReactNode;
}

export interface MenuGroupProps {
  /**
   * A heading grouping the menu items.
   * @external
   */
  title?: ReactNode;
  /** Child items that belong to the group. */
  children: ReactNode;
  /**
   * An accessibility label for the group.
   * @external
   */
  'aria-label'?: string;
}

export interface MenuItemProps {
  /** Rendered contents of the item */
  children: ReactNode;
  /**
   * Handler that is called when an item is selected.
   * @external
   */
  onAction?: (key: CollectionItemId) => void;
  /**
   * The id of the item. This will be passed into the `onAction` handler of `Menu`
   * @external
   */
  id?: string;
  /**
   * Marks an item as disabled. Disabled items cannot be selected, focused, or otherwise interacted with.
   * @external
   */
  disabled?: boolean;
  /**
   * An accessibility label for this item.
   * @external
   */
  'aria-label'?: string;
  /**
   * The content of the MenuItem as text. Useful if the MenuItem contains
   * nested children, but you still want it to be accessible via typeahead.
   * @external
   */
  textValue?: string;
  /**
   * The type of the item
   * @external
   */
  type?: 'default' | 'destructive';
}

interface MenuContextValue {
  id?: string;
  onClose?: () => void;
  state?: TreeState<unknown>;
  onAction?: ActionCallback;
}

const MenuContext = React.createContext<MenuContextValue>({});

function useMenuContext(): MenuContextValue {
  return useContext(MenuContext);
}

/** @external */
export const MenuItem = view.li.create<MenuItemProps>(function MenuItem(
  {onAction, disabled, id, children, textValue, type, ...props},
  View,
) {
  const ref = React.createRef<HTMLLIElement>();
  const {onClose, onAction: onMenuAction, state} = useMenuContext();
  const {key} = useItem({
    'aria-label': props['aria-label'] ?? '',
    typeaheadValue:
      typeof children === 'string'
        ? children
        : textValue ?? props['aria-label'] ?? '',
    id,
  });

  if (state == null) {
    throw new Error('<MenuItem> rendered outside <Menu>');
  }

  const {menuItemProps} = useMenuItem(
    {
      key,
      onAction: () => {
        onAction?.(key);
        onMenuAction?.(key);
      },
      onClose,
      isDisabled: disabled,
      closeOnSelect: true,
      'aria-label': props['aria-label'],
    },
    state,
    ref,
  );

  const {isFocusVisible, focusProps} = useFocusRing();
  const modality = isFocusVisible ? 'keyboard' : 'pointer';

  return (
    <View
      uses={[
        props,
        menuItemProps,
        focusProps,
        Themed.MenuItem,
        Themed.MenuItemVariants.assign({modality, disabled, type}),
      ]}
      ref={ref}
    >
      {children}
    </View>
  );
});

/** @external */
export const MenuGroup = view.li.create<MenuGroupProps>(function MenuGroup({
  title,
  children,
  ...props
}) {
  const {key} = useItem({
    'aria-label': props['aria-label'] ?? '',
    type: 'section',
  });

  const {state} = useMenuContext();

  const {itemProps, headingProps, groupProps} = useMenuSection({
    heading: title,
    'aria-label': props['aria-label'],
  });

  if (state == null) {
    throw new Error('<MenuGroup> rendered outside <Menu>');
  }

  return (
    <>
      {key !== state.collection.getFirstKey() ? (
        <view.li role="separator" uses={[Themed.MenuItemSeparator]} />
      ) : null}
      <view.li uses={[props, itemProps, Themed.MenuGroup]}>
        {title ? (
          <view.span uses={[headingProps, Themed.MenuGroupHeading]}>
            {title}
          </view.span>
        ) : null}
        <view.ul uses={[groupProps, Themed.MenuGroupItems]}>
          <IndexedGroup>{children}</IndexedGroup>
        </view.ul>
      </view.li>
    </>
  );
});

interface MenuItems extends MenuProps, MenuContextValue {}

const MenuItems = view.ul.create(function MenuItems(
  {children, onAction, ...props}: MenuItems,
  View,
) {
  const ref = useRef<HTMLUListElement>(null);
  const didFocusRef = useRef<boolean>(false);
  const collection = useCollection(children);
  const {onClose} = useMenuContext();

  const state = useTreeState({
    getItems: collection.getItems,
  });

  const {menuProps} = useMenu(
    {
      autoFocus: 'first',
      ...props,
    },
    state,
    ref,
  );

  // this recreates `autoFocus` behavior in `useMenu`. because we do two-pass
  // rendering to get the collection children, the collection is empty the first
  // time we render the menu, and there's nothing to focus.
  React.useEffect(() => {
    const walkForKey = (key: CollectionItemId): CollectionItemId | null => {
      const item = state.collection.getItem(key);
      if (item) {
        if (item.type === 'item') {
          return item.key;
        } else if (item.nextKey) {
          return walkForKey(item.nextKey);
        }
      }
      return null;
    };

    const collectionFirstKey = state.collection.getFirstKey();
    if (collectionFirstKey) {
      const firstKey = walkForKey(collectionFirstKey);
      if (didFocusRef.current === false && typeof firstKey === 'string') {
        state.selectionManager.setFocusedKey(firstKey);
        state.selectionManager.setFocused(true);
        didFocusRef.current = true;
      }
    }
  }, [state]);

  return (
    <CollectionProvider collection={collection}>
      <View
        ref={ref}
        uses={[
          menuProps,
          Themed.Menu,
          provider(MenuContext, {
            state,
            onClose,
            onAction,
          }),
        ]}
      >
        {collection.children}
      </View>
    </CollectionProvider>
  );
});

/** @external */
export const Menu = view.ul.create(function Menu(props: MenuProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const state = useMenuTriggerState({
    onOpenChange: (isOpen) => {
      props.onOpenChange?.(isOpen);
      !isOpen && props.onClose?.();
    },
  });
  const {menuTriggerProps, menuProps} = useMenuTrigger({}, state, triggerRef);
  const onClose = state.close;

  // if there is no trigger element, fall back to rendering the menu inline in an open state
  if (!props.trigger) {
    return <MenuItems uses={[props]}>{props.children}</MenuItems>;
  }

  // if there *is* a trigger, render the menu relative to the trigger (via a popover)
  return (
    <Fragment>
      <PressResponder
        // trigger props contain aria-{expanded|haspopup|controls|pressed}
        {...menuTriggerProps}
        ref={triggerRef}
        isPressed={state.isOpen}
        // this is to fix https://jira.corp.stripe.com/browse/SAIL-2565
        // and can be removed if we migrate from usePress
        // @ts-expect-error need to put inline styles on this component, confirmed they get passed down
        style={{userSelect: 'none'}}
      >
        {props.trigger}
      </PressResponder>
      <MenuContext.Provider value={{onClose}}>
        {state.isOpen ? (
          // Using deprecated Popover, not the new one
          // eslint-disable-next-line @sail/ui-no-conditional-rendering
          <Popover
            targetRef={triggerRef}
            placement="bottom"
            onClose={onClose}
            shouldCloseOnBlur
          >
            <FocusScope restoreFocus>
              <view.button
                type="button"
                tabIndex={-1}
                aria-label="Dismiss"
                onClick={onClose}
                uses={[Themed.visuallyHidden]}
              />
              {/* @ts-expect-error MenuItems doesn't like having menuProps on it */}
              <MenuItems uses={[props, menuProps]}>{props.children}</MenuItems>
              <view.button
                type="button"
                tabIndex={-1}
                aria-label="Dismiss"
                onClick={onClose}
                uses={[Themed.visuallyHidden]}
              />
            </FocusScope>
          </Popover>
        ) : null}
      </MenuContext.Provider>
    </Fragment>
  );
});
